'use client';

import { Breadcrumb } from '@/components/common/breadcrumb';
import { PageHeader } from '@/components/common/page-header';
import { menuBreadcrumbItems } from '@/config/menu-catalog';
import { ExpandableCard } from '@/components/ui/expandable-card';
import { useOrganization } from '@/contexts/organization-context';
import { useToast } from '@/hooks/use-toast';
import { apiClient } from '@/lib/api-client';
import {
  AggregateDto,
  PetrohqApiResponseListAggregateDto,
} from '@/lib/api/data-contracts';
import { useFilterStore } from '@/stores/filter-store';
import { useCallback, useEffect, useState } from 'react';
import * as RealGrid from 'realgrid';
import { RealGridReact, RGDataColumn, RGDataField } from 'realgrid-react';

export function Aggregate() {
  const { toast } = useToast();
  const { getAllStores } = useOrganization();
  // 온디맨드 방식: API 호출 시점에만 필터 상태 읽기

  // 상태 관리
  const [aggregateData, setAggregateData] = useState<AggregateDto[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // 집계 데이터 가져오기 함수
  const fetchAggregateData = useCallback(async () => {
    // API 호출 시점에 현재 필터 상태 읽기
    const filters = useFilterStore.getState();

    if (!filters.dateRange?.start || !filters.dateRange?.end) {
      return;
    }

    setIsLoading(true);

    const storeCodeList =
      filters.store.length > 0 ? filters.store : getAllStores();

    const params = new URLSearchParams();

    storeCodeList.forEach(storeCode => {
      params.append('storeCodeList', storeCode);
    });

    // 날짜 필터 (필수)
    params.append('startDate', filters.dateRange.start.replace(/-/g, ''));
    params.append('endDate', filters.dateRange.end.replace(/-/g, ''));

    try {
      const response = await apiClient.get('sales/aggregate', {
        searchParams: params,
      });
      const data =
        (await response.json()) as PetrohqApiResponseListAggregateDto;

      if (data.result) {
        setAggregateData(data.result);
      }
    } catch (error) {
      console.error('Aggregate data fetch error:', error);
      console.error('Error details:', {
        url: 'sales/aggregate',
        params: Object.fromEntries(params.entries()),
        error: error instanceof Error ? error.message : error,
      });
      toast({
        title: '조회 실패',
        description: '종합일보 데이터를 불러오는 중 오류가 발생했습니다',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  }, [getAllStores, toast]);

  // 조회 버튼 클릭 핸들러
  const handleSearch = () => {
    fetchAggregateData();
  };

  // 초기 데이터 로드
  useEffect(() => {
    fetchAggregateData();
  }, []);

  const breadcrumb = <Breadcrumb items={menuBreadcrumbItems('aggregate')} />;

  const pageHeader = (
    <PageHeader onSearch={handleSearch} isLoading={isLoading} />
  );

  return (
    <div className='h-full'>
      <ExpandableCard
        title='종합일보'
        breadcrumb={breadcrumb}
        pageHeader={pageHeader}
        fill
      >
        <div
          className='border rounded-lg flex-1 min-h-0'
        >
          <div className='w-full h-full'>
            <RealGridReact
              gridProps={{
                display: { fitStyle: RealGrid.GridFitStyle.FILL },
                checkBar: { visible: false },
                stateBar: { visible: false },
                edit: { readOnly: true, editable: false },
                groupPanel: { visible: false },
                rowGroup: {
                  sorting: false,
                },
                footer: {
                  visible: true,
                },
                sorting: {
                  enabled: true,
                  showSortOrder: true,
                  style: RealGrid.SortStyle.INCLUSIVE,
                },
              }}
              rows={aggregateData}
            >
              {/* Data Fields */}
              <RGDataField
                fieldName='compCd'
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName='compName'
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName='storeCd'
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName='storeName'
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName='oilCode'
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName='oilName'
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName='oilKind'
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName='oilKindName'
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName='qty'
                dataType={RealGrid.ValueType.NUMBER}
              />
              <RGDataField
                fieldName='drum'
                dataType={RealGrid.ValueType.NUMBER}
              />
              <RGDataField
                fieldName='avgPrice'
                dataType={RealGrid.ValueType.NUMBER}
              />
              <RGDataField
                fieldName='amount'
                dataType={RealGrid.ValueType.NUMBER}
              />
              <RGDataField
                fieldName='supplyPrice'
                dataType={RealGrid.ValueType.NUMBER}
                valueExpression="values['amount'] - (values['amount'] / 11)"
              />
              <RGDataField
                fieldName='vat'
                dataType={RealGrid.ValueType.NUMBER}
                valueExpression="values['amount'] / 11"
              />
              <RGDataField
                fieldName='cashAmount'
                dataType={RealGrid.ValueType.NUMBER}
              />
              <RGDataField
                fieldName='cashAmount_calculated'
                dataType={RealGrid.ValueType.NUMBER}
                valueExpression="values['amount'] - values['creditCardAmount'] - values['onCreditAmount'] - values['giftCardAmount']"
              />
              <RGDataField
                fieldName='creditCardAmount'
                dataType={RealGrid.ValueType.NUMBER}
              />
              <RGDataField
                fieldName='onCreditAmount'
                dataType={RealGrid.ValueType.NUMBER}
              />
              <RGDataField
                fieldName='giftCardAmount'
                dataType={RealGrid.ValueType.NUMBER}
              />
              <RGDataField
                fieldName='accumulatedQty'
                dataType={RealGrid.ValueType.NUMBER}
              />
              <RGDataField
                fieldName='accumulatedDrum'
                dataType={RealGrid.ValueType.NUMBER}
              />
              <RGDataField
                fieldName='accumulatedAmount'
                dataType={RealGrid.ValueType.NUMBER}
              />

              {/* Data Columns */}
              <RGDataColumn
                fieldName='storeName'
                header={{ text: '매장' }}
                mergeRule='value'
              />
              <RGDataColumn fieldName='oilCode' header={{ text: '상품코드' }} />
              <RGDataColumn fieldName='oilName' header={{ text: '상품명' }} />
              <RGDataColumn fieldName='oilKind' visible={false} />
              <RGDataColumn
                fieldName='oilKindName'
                header={{ text: '상품구분' }}
                groupFooter={{
                  valueCallback: function (
                    _grid: any,
                    _column: any,
                    _footerIndex: number,
                    group: any
                  ) {
                    return group.groupValue + ' 소계: ';
                  },
                }}
                footer={{ text: '총계 :' }}
              />
              <RGDataColumn
                fieldName='qty'
                header={{ text: '수량' }}
                numberFormat='#,##0'
                styleName='text-right'
                footer={{
                  numberFormat: '#,##0.0',
                  valueCallback: function (
                    grid: any,
                    _column: any,
                    _footerIndex: number,
                    _group: any
                  ) {
                    const itemCount = grid.getItemCount();
                    let sum = 0;
                    for (let i = 0; i < itemCount; i++) {
                      const oilKind = grid.getValue(i, 'oilKind');
                      const qty = grid.getValue(i, 'qty');
                      if (oilKind === '1' || oilKind === '2') {
                        sum += qty;
                      }
                    }
                    return sum;
                  },
                }}
                groupFooter={{ expression: 'sum', numberFormat: '#,##0' }}
              />
              <RGDataColumn
                fieldName='drum'
                header={{ text: 'DM/Ton' }}
                numberFormat='#,##0.0'
                styleName='text-right'
                footer={{ expression: 'sum', numberFormat: '#,##0.0' }}
                groupFooter={{ expression: 'sum', numberFormat: '#,##0.0' }}
              />
              <RGDataColumn
                fieldName='avgPrice'
                header={{ text: '평균단가' }}
                numberFormat='#,##0'
                styleName='text-right'
              />
              <RGDataColumn
                fieldName='amount'
                header={{ text: '판매금액' }}
                numberFormat='#,##0'
                styleName='text-right'
                footer={{ expression: 'sum', numberFormat: '#,##0' }}
                groupFooter={{ expression: 'sum', numberFormat: '#,##0' }}
              />
              <RGDataColumn
                fieldName='supplyPrice'
                header={{ text: '공급가액' }}
                numberFormat='#,##0'
                styleName='text-right'
                footer={{ expression: 'sum', numberFormat: '#,##0' }}
                groupFooter={{ expression: 'sum', numberFormat: '#,##0' }}
              />
              <RGDataColumn
                fieldName='vat'
                header={{ text: '세액' }}
                numberFormat='#,##0'
                styleName='text-right'
                footer={{ expression: 'sum', numberFormat: '#,##0' }}
                groupFooter={{ expression: 'sum', numberFormat: '#,##0' }}
              />
              <RGDataColumn
                fieldName='cashAmount_calculated'
                header={{ text: '현금판매' }}
                numberFormat='#,##0'
                styleName='text-right'
                footer={{ expression: 'sum', numberFormat: '#,##0' }}
                groupFooter={{ expression: 'sum', numberFormat: '#,##0' }}
              />
              <RGDataColumn
                fieldName='creditCardAmount'
                header={{ text: '신용카드판매' }}
                numberFormat='#,##0'
                styleName='text-right'
                footer={{ expression: 'sum', numberFormat: '#,##0' }}
                groupFooter={{ expression: 'sum', numberFormat: '#,##0' }}
              />
              <RGDataColumn
                fieldName='onCreditAmount'
                header={{ text: '외상판매' }}
                numberFormat='#,##0'
                styleName='text-right'
                footer={{ expression: 'sum', numberFormat: '#,##0' }}
                groupFooter={{ expression: 'sum', numberFormat: '#,##0' }}
              />
              <RGDataColumn
                fieldName='giftCardAmount'
                header={{ text: '상품권판매' }}
                numberFormat='#,##0'
                styleName='text-right'
                footer={{ expression: 'sum', numberFormat: '#,##0' }}
                groupFooter={{ expression: 'sum', numberFormat: '#,##0' }}
              />
              <RGDataColumn
                fieldName='accumulatedQty'
                header={{ text: '수량누계(월)' }}
                numberFormat='#,##0'
                styleName='text-right'
                footer={{ expression: 'sum', numberFormat: '#,##0' }}
                groupFooter={{ expression: 'sum', numberFormat: '#,##0' }}
              />
              <RGDataColumn
                fieldName='accumulatedDrum'
                header={{ text: 'DM/Ton(월)' }}
                numberFormat='#,##0.0'
                styleName='text-right'
                footer={{ expression: 'sum', numberFormat: '#,##0.0' }}
                groupFooter={{ expression: 'sum', numberFormat: '#,##0.0' }}
              />
              <RGDataColumn
                fieldName='accumulatedAmount'
                header={{ text: '금액누계(월)' }}
                numberFormat='#,##0'
                styleName='text-right'
                footer={{ expression: 'sum', numberFormat: '#,##0' }}
                groupFooter={{ expression: 'sum', numberFormat: '#,##0' }}
              />
            </RealGridReact>
          </div>
        </div>

        <div className='mt-4 flex items-center justify-between'>
          <div className='flex items-center space-x-4'>
            <span className='text-sm text-muted-foreground'>
              총 {aggregateData.length}건
            </span>
            {isLoading && (
              <span className='text-sm text-blue-600'>로딩 중...</span>
            )}
          </div>
        </div>
      </ExpandableCard>
    </div>
  );
}
